import {
  CREDITS_POOL_STATE,
  DISCLAIMER_ACTION_KIND,
  LICENSE_STATE
} from 'src/types/enum';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';

/**
 * `PAYG_SETTINGS` links to /settings/pay-as-you-go, `CONTACT_SUPPORT` opens a
 * `mailto:` — one field rather than two flags so a case cannot claim both.
 */
export interface DisclaimerAction {
  kind: DISCLAIMER_ACTION_KIND;
  labelKey: string;
}

export interface DisclaimerConfig {
  messageKey: string;
  action: DisclaimerAction | null;
  isWarning: boolean;
  dismissible: boolean;
}

type Situation =
  | 'standard_in_plan_nearing_limit'
  | 'standard_in_plan_limit_reached'
  | 'payg_available_in_plan_nearing_limit'
  | 'payg_available_in_plan_limit_reached'
  | 'payg_active_in_plan_nearing_limit'
  | 'payg_active_payg_nearing_limit'
  | 'payg_active_both_nearing_limit'
  | 'payg_active_payg_limit_reached'
  | 'payg_active_both_limit_reached';

const BLOCKED: ReadonlySet<Situation> = new Set<Situation>([
  'standard_in_plan_limit_reached',
  'payg_available_in_plan_limit_reached',
  'payg_active_both_limit_reached'
]);

const NO_ACTION: ReadonlySet<Situation> = new Set<Situation>([
  'payg_active_in_plan_nearing_limit'
]);

const MESSAGES: Record<Situation, { owner: string; user: string }> = {
  standard_in_plan_nearing_limit: {
    owner: T.licenseDisclaimerStandard90Owner,
    user: T.licenseDisclaimerStandard90User
  },
  standard_in_plan_limit_reached: {
    owner: T.licenseDisclaimerStandard100Owner,
    user: T.licenseDisclaimerStandard100User
  },
  payg_available_in_plan_nearing_limit: {
    owner: T.licenseDisclaimerPaygAvailable90Owner,
    user: T.licenseDisclaimerPaygAvailable90User
  },
  payg_available_in_plan_limit_reached: {
    owner: T.licenseDisclaimerPaygAvailable100Owner,
    user: T.licenseDisclaimerPaygAvailable100User
  },
  payg_active_in_plan_nearing_limit: {
    owner: T.licenseDisclaimerPaygCovering,
    user: T.licenseDisclaimerPaygCovering
  },
  payg_active_payg_nearing_limit: {
    owner: T.licenseDisclaimerPayg90Owner,
    user: T.licenseDisclaimerPayg90User
  },
  payg_active_both_nearing_limit: {
    owner: T.licenseDisclaimerBoth90Owner,
    user: T.licenseDisclaimerBoth90User
  },
  payg_active_payg_limit_reached: {
    owner: T.licenseDisclaimerPayg100Owner,
    user: T.licenseDisclaimerPayg100User
  },
  payg_active_both_limit_reached: {
    owner: T.licenseDisclaimerBoth100Owner,
    user: T.licenseDisclaimerBoth100User
  }
};

const PAYG_SETTINGS = (labelKey: string): DisclaimerAction => ({
  kind: DISCLAIMER_ACTION_KIND.PAYG_SETTINGS,
  labelKey
});

const CONTACT_SUPPORT: DisclaimerAction = {
  kind: DISCLAIMER_ACTION_KIND.CONTACT_SUPPORT,
  labelKey: T.contactCustomerSupport
};

const OWNER_ACTION: Record<
  LICENSE_STATE,
  (blocked: boolean) => DisclaimerAction | null
> = {
  [LICENSE_STATE.STANDARD]: (blocked) => (blocked ? CONTACT_SUPPORT : null),
  [LICENSE_STATE.PAYG_AVAILABLE]: () =>
    PAYG_SETTINGS(T.licenseDisclaimerEnablePayg),
  [LICENSE_STATE.PAYG_ACTIVE]: (blocked) =>
    PAYG_SETTINGS(
      blocked ? T.licenseDisclaimerRestoreAccess : T.licenseDisclaimerRaiseCap
    )
};

const resolveSituation = (
  tier: LICENSE_STATE,
  plan: CREDITS_POOL_STATE,
  payg: CREDITS_POOL_STATE
): Situation | null => {
  if (tier === LICENSE_STATE.PAYG_ACTIVE) {
    if (payg === CREDITS_POOL_STATE.OK) {
      return plan === CREDITS_POOL_STATE.NEARING_LIMIT
        ? 'payg_active_in_plan_nearing_limit'
        : null;
    }
    if (payg === CREDITS_POOL_STATE.LIMIT_REACHED) {
      return plan === CREDITS_POOL_STATE.LIMIT_REACHED
        ? 'payg_active_both_limit_reached'
        : 'payg_active_payg_limit_reached';
    }
    return plan === CREDITS_POOL_STATE.OK
      ? 'payg_active_payg_nearing_limit'
      : 'payg_active_both_nearing_limit';
  }

  if (plan === CREDITS_POOL_STATE.OK) {
    return null;
  }
  const planSpent = plan === CREDITS_POOL_STATE.LIMIT_REACHED;
  if (tier === LICENSE_STATE.STANDARD) {
    return planSpent
      ? 'standard_in_plan_limit_reached'
      : 'standard_in_plan_nearing_limit';
  }
  return planSpent
    ? 'payg_available_in_plan_limit_reached'
    : 'payg_available_in_plan_nearing_limit';
};

export interface DisclaimerInput {
  tier: LICENSE_STATE;
  plan: CREDITS_POOL_STATE;
  payg: CREDITS_POOL_STATE;
  isOwner: boolean;
}

export const getDisclaimerConfig = ({
  tier,
  plan,
  payg,
  isOwner
}: DisclaimerInput): DisclaimerConfig | null => {
  const situation = resolveSituation(tier, plan, payg);
  if (!situation) {
    return null;
  }

  const blocked = BLOCKED.has(situation);
  return {
    messageKey: MESSAGES[situation][isOwner ? 'owner' : 'user'],
    action:
      isOwner && !NO_ACTION.has(situation) ? OWNER_ACTION[tier](blocked) : null,
    isWarning: !blocked,
    dismissible: !blocked && isOwner
  };
};
